import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useDebouncedValue } from "@/features/create-claim/hooks/use-debounced-value";
import { useReferenceSearchClient } from "@/features/search/api/use-scoped-search-key";
import { config } from "@/lib/config";
import { queryKeys } from "@/lib/query-keys";

/** Shape of the Algolia airline index hits (SearchAirline.vue). */
export interface AirlineHit {
  objectID: string;
  id: string;
  name: string;
  iata?: string | null;
  icao?: string | null;
  isEC261?: boolean;
  isActive?: boolean;
  iconURL?: string | null;
}

/** Airline lookup against the Algolia airline index via the reference-index client (active airlines only). */
export function useAirlineSearch(query: string) {
  const { client } = useReferenceSearchClient();
  const debouncedQuery = useDebouncedValue(query, 250);

  return useQuery({
    queryKey: [queryKeys.search.scopedKey[0], "airlines", debouncedQuery],
    enabled: client !== null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<AirlineHit[]> => {
      if (!client) {
        throw new Error("Search client not ready");
      }
      const { results } = await client.search<AirlineHit>({
        requests: [
          {
            indexName: config.ALGOLIA_AIRLINE_INDEX,
            query: debouncedQuery,
            filters: "isActive:true",
            hitsPerPage: 8,
          },
        ],
      });
      const first = results[0];
      return first && "hits" in first ? (first.hits as AirlineHit[]) : [];
    },
  });
}
